import * as React from 'react';

import { useLatestRef } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { useOnClickOutside } from '../Events';
import { InteractionLocks, useFirstResponderInteraction } from '../Interactive';
import type { PortalAbsoluteRef } from './PortalAbsolute';

export interface CloseableCriterias {
    /**
     * If true, the portal will close when pressing the escape key.
     * @default true
     */
    closeOnEsc?: boolean;

    /**
     * If true, the portal will close when clicking outside of it.
     * @default true
     */
    closeOnOutsideClick?: boolean;

    /**
     * If true, the portal will close when clicking in a portal nested in this one.
     * @default true
     */
    closeOnClickInNestedPortals?: boolean;

    /**
     * If true, the portal will close when scrolling the document.
     * @default true
     */
    closeOnScroll?: boolean;

    /**
     * If true, the portal will close when clicking on a portal descendant.
     * @default false
     */
    closeOnClick?: boolean;
}

export interface CloseableCauses {
    onEsc?: boolean;
    onOutsideClick?: boolean;
    onScroll?: boolean;
    onClick?: boolean;
}

export type CloseableOptions = CloseableCriterias & {
    opened: boolean;
    /**
     * Use capture phase for the onKeyDown event.
     * @default true
     */
    capture?: boolean;
    onClose: (cause?: CloseableCauses) => void;
};

/*
 * Add auto-closing behavior to a Portal.
 * Triggers the provided onClose when it should be closed.
 */
export function useCloseable(
    options: CloseableOptions,
    portalRef?: React.RefObject<PortalAbsoluteRef | undefined>,
    anchorRef?: React.RefObject<any>
) {
    const {
        onClose,
        opened = true,
        closeOnEsc = true,
        closeOnOutsideClick = true,
        closeOnClickInNestedPortals = true,
        closeOnScroll = true,
        closeOnClick = false,
        capture = true,
    } = options;

    const onCloseRef = useLatestRef(onClose);

    const isFirstEscapeResponder = useFirstResponderInteraction(
        InteractionLocks.KEYBOARD_ESCAPE,
        opened && closeOnEsc
    );

    // Close on scroll
    React.useEffect(() => {
        if (!closeOnScroll || !opened) {
            return undefined;
        }

        let closed = false;

        const onScroll = (event: Event) => {
            if (closed) {
                return;
            }

            // Check if we are scrolling outside the portal
            // Inner scrolling should not close it
            const container = portalRef?.current?.getContainer();
            if (!container || !container.contains(event.target as Node)) {
                onCloseRef.current({
                    onScroll: true,
                });
                closed = true;
            }
        };

        // We use capture to ensure that the scroll event is fired before
        // the RNW scroll view.
        window.addEventListener('scroll', onScroll, true);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [opened, closeOnScroll]);

    // Close on escape
    React.useEffect(() => {
        if (!isFirstEscapeResponder) {
            return;
        }

        const onDocumentKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && portalRef?.current && !event.defaultPrevented) {
                event.preventDefault();
                event.stopPropagation();
                onCloseRef.current({
                    onEsc: true,
                });
            }
        };

        document.addEventListener('keydown', onDocumentKeyDown, { capture });
        return () => {
            document.removeEventListener('keydown', onDocumentKeyDown, { capture });
        };
    }, [isFirstEscapeResponder, onCloseRef]);

    // Close on click in the portal itself
    React.useEffect(() => {
        const portal = portalRef?.current;
        if (!closeOnClick || !portal || !opened) {
            return undefined;
        }

        const portalRoot = portal.getContainer();
        if (!portalRoot) {
            return undefined;
        }

        let clicked = false;

        // We listen to click on document before react triggers its onClick
        const onSelfClick = (event: MouseEvent) => {
            clicked = event.target instanceof HTMLElement && portalRoot.contains(event.target);
        };

        // But we close it after react triggers it
        const onSelfClickAfterReact = () => {
            if (clicked) {
                // Clicked on an element inside the portal
                onCloseRef.current({
                    onClick: true,
                });
            }
        };

        document.addEventListener('click', onSelfClick, true);
        document.addEventListener('click', onSelfClickAfterReact);
        return () => {
            document.removeEventListener('click', onSelfClick, true);
            document.removeEventListener('click', onSelfClickAfterReact);
        };
    }, [opened, closeOnClick]);

    // Close on outside click
    useOnClickOutside(
        () => {
            const portal = portalRef?.current;
            const anchor = anchorRef ? anchorRef.current : null;
            const portalRoot = portal ? portal.getContainer() : null;

            const elements = [portalRoot, anchor].filter(filterOutNullable);

            if (!closeOnClickInNestedPortals && portalRoot) {
                // If we don't want to close on click in nested portals,
                // we need to exclude them from the list of elements.
                elements.push(...getNextSiblings(portalRoot));
            }

            return elements;
        },
        closeOnOutsideClick && opened
            ? () => {
                  onCloseRef.current({
                      onOutsideClick: true,
                  });
              }
            : undefined
    );
}

function getNextSiblings(element: HTMLElement): HTMLElement[] {
    const siblings: HTMLElement[] = [];
    let sibling = element.nextElementSibling;
    while (sibling) {
        siblings.push(sibling as HTMLElement);
        sibling = sibling.nextElementSibling;
    }

    return siblings;
}
